import './styles.css';

import { assertEnvVar, assertNonNull } from '@h5web/app';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';
import { AuthProvider } from 'react-oidc-context';
import { Route, Switch } from 'wouter';

import App from './App';
import ErrorFallback from './ErrorFallback';
import NotFound from './NotFound';
import SingleSignOn from './SingleSignOn';
import { removeSignInParams } from './utils';

const SSO_AUTHORITY_URL = import.meta.env.VITE_SSO_AUTHORITY_URL;
const SSO_CLIENT_ID = import.meta.env.VITE_SSO_CLIENT_ID;
assertEnvVar(SSO_AUTHORITY_URL, 'VITE_SSO_AUTHORITY_URL');
assertEnvVar(SSO_CLIENT_ID, 'VITE_SSO_CLIENT_ID');

const rootElem = document.querySelector('#root');
assertNonNull(rootElem);

const queryClient = new QueryClient({
  // https://tanstack.com/query/latest/docs/framework/react/guides/important-defaults
  defaultOptions: {
    queries: {
      networkMode: 'always', // always fire queries regardless of online/offline status
      staleTime: 'static', // never refresh
      gcTime: Infinity, // never clean cache
      retry: false, // never retry fetching automatically on error
      structuralSharing: false, // not relevant since data is never refetched
    },
  },
});

createRoot(rootElem).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary
        FallbackComponent={ErrorFallback}
        onError={() => {
          queryClient.removeQueries({
            predicate: (query) => query.state.status === 'error',
          });
        }}
      >
        <Switch>
          <Route path="/public" nest>
            <App />
          </Route>
          <Route path="/private" nest>
            <AuthProvider
              authority={SSO_AUTHORITY_URL}
              client_id={SSO_CLIENT_ID}
              redirect_uri={globalThis.location.href}
              onSigninCallback={removeSignInParams}
            >
              <SingleSignOn>
                <App />
              </SingleSignOn>
            </AuthProvider>
          </Route>
          <Route>
            <NotFound />
          </Route>
        </Switch>
      </ErrorBoundary>
    </QueryClientProvider>
  </StrictMode>,
);
